// Iter 31.256m — Gary's Shed public renderer.
//
// Lives at /garys-shed (index) and /garys-shed/:slug (page).
// The Cloudflare Worker on garys-shed.cloud reverse-proxies
// garys-shed.cloud/* → spyfinance.cloud/garys-shed/*, so visitors
// see this UI under the masked hostname. Two manages content via
// the `update_garys_shed_page` MCP tool — there is no UI form here.
//
// Public, no auth.

import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 31.262 — host-aware route prefix. On garys-shed.cloud the SPA
// is mounted at "/", so internal markdown links like `/the-agents`
// resolve directly. On spyfinance.cloud the same SPA is mounted at
// "/garys-shed", so the same link needs the prefix. Recomputed once
// at module load so the markdown renderer below stays cheap.
function _routePrefix() {
    if (typeof window === "undefined") return "/garys-shed";
    return window.location.hostname === "garys-shed.cloud" ? "" : "/garys-shed";
}

// Markdown link component. Two writes internal links as `/the-agents`
// (the public-host form). On the canonical host that's fine; on the
// preview / spyfinance host we rewrite to `/garys-shed/the-agents`
// and route via react-router's <Link> for client-side nav (no full
// page reload). External links pass through to a normal <a> with
// noopener/noreferrer. Anchors (`#section`) stay as plain anchors.
function MarkdownLink({ href, children, ...rest }) {
    if (!href) return <span {...rest}>{children}</span>;
    const isInternal = href.startsWith("/") && !href.startsWith("//");
    const isAnchor = href.startsWith("#");
    if (isAnchor) {
        return <a href={href} {...rest}>{children}</a>;
    }
    if (isInternal) {
        const prefix = _routePrefix();
        // Avoid double-prefixing if Two ever files a fully-prefixed link.
        const path = href.startsWith(prefix + "/") || href === prefix
            ? href
            : prefix + href;
        return (
            <Link
                to={path}
                className="text-ink underline decoration-stamp-red decoration-1 underline-offset-2 hover:text-stamp-red transition-colors"
                {...rest}
            >
                {children}
            </Link>
        );
    }
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink underline decoration-stamp-red decoration-1 underline-offset-2 hover:text-stamp-red transition-colors"
            {...rest}
        >
            {children}
        </a>
    );
}

// Iter 31.278 — `rehypeRaw` lets raw HTML embedded in the markdown body
// (e.g. `<img src="..." style="max-height:400px" />`) render as actual
// HTML instead of escaped text. Without it react-markdown drops inline
// HTML on the floor. Two files site content via MCP — any richer-than-
// markdown formatting (image size caps, embed blocks) rides through as
// raw HTML nodes, so this plugin is now mandatory for both renderers.
const _MD_COMPONENTS = { a: MarkdownLink };
const _REHYPE_PLUGINS = [rehypeRaw];

// Iter 31.263 — tiny imperative head-tag manager. No react-helmet
// dependency; we just write `document.title` and toggle a few <meta>
// tags directly. Pure UX improvement for the in-browser tab + back-
// button experience. Server-side unfurlers (Slack, Discord, iMessage)
// still need OG injection at the Cloudflare Worker layer because they
// never run our JS — that's a separate follow-up.
function _setMeta(name, content, attr = "name") {
    if (typeof document === "undefined") return;
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function useDocumentMeta({ title, description }) {
    useEffect(() => {
        if (typeof document === "undefined") return;
        const prevTitle = document.title;
        if (title) document.title = title;
        if (description) {
            _setMeta("description", description);
            _setMeta("og:title", title || prevTitle, "property");
            _setMeta("og:description", description, "property");
            _setMeta("og:type", "article", "property");
            _setMeta("twitter:card", "summary");
            _setMeta("twitter:title", title || prevTitle);
            _setMeta("twitter:description", description);
        }
        return () => { document.title = prevTitle; };
    }, [title, description]);
}

export function GarysShedIndex() {
    const [pages, setPages] = useState(null);
    const [indexPage, setIndexPage] = useState(null);
    const [err, setErr] = useState("");
    // Iter 31.280 — Two spotted the origin's raw page list flashing for
    // ~500ms during hydration on garys-shed.cloud. Root cause was
    // sequential fetches: `/pages` lands before `/page/index`, so
    // between them we render the fallback list even though Two has
    // filed an index page. Fix: fire both fetches in parallel via
    // Promise.allSettled and only reveal the render tree once BOTH
    // have settled. Nothing flashes because there's nothing to
    // display in the interim.
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [pagesRes, indexRes] = await Promise.allSettled([
                axios.get(`${API}/api/garys-shed/pages`),
                axios.get(`${API}/api/garys-shed/page/index`),
            ]);
            if (cancelled) return;
            if (pagesRes.status === "fulfilled") {
                setPages(pagesRes.value.data.pages || []);
            } else {
                setErr(`Couldn't load pages: ${pagesRes.reason?.message || "unknown"}`);
            }
            if (indexRes.status === "fulfilled") {
                setIndexPage(indexRes.value.data);
            }
            // ^ index 404 is fine — that's the list-only fallback case.
            setReady(true);
        })();
        return () => { cancelled = true; };
    }, []);

    // Iter 31.262 — when Two has filed an index page, it IS the
    // navigation surface. The auto-generated list (a) duplicates
    // what's already in the curated body and (b) leaks every slug
    // including drafts / Cold Claude's scaffolding pages. So we
    // suppress it entirely when indexPage exists. The list returns
    // only as a fallback for the no-index case.
    const fallbackList = useMemo(() => {
        if (indexPage) return [];
        return (pages || []).filter((p) => p.slug !== "index");
    }, [pages, indexPage]);

    const prefix = _routePrefix();

    useDocumentMeta({
        title: indexPage?.title
            ? `${indexPage.title} · Gary's Shed`
            : "Gary's Shed",
        description: indexPage?.og_description || "A space.",
    });

    return (
        <div className="min-h-screen bg-paper text-ink public-site" data-testid="garys-shed-index">
            <div
                className={`max-w-2xl mx-auto px-6 py-16 transition-opacity duration-[150ms] ${
                    ready ? "opacity-100" : "opacity-0"
                }`}
            >
                <h1 className="font-heading uppercase tracking-[0.22em] text-3xl mb-3" data-testid="garys-shed-title">
                    Gary&apos;s Shed
                </h1>
                <p className="font-body text-sm text-ink-soft mb-12 leading-relaxed">
                    A space.
                </p>

                {!ready ? (
                    // Iter 31.280 — silent skeleton. No text, no list, no
                    // list-flash. `data-testid` present so testing agents
                    // can still assert the pre-hydration slot.
                    <div
                        className="font-mono text-xs text-ink-soft opacity-0"
                        data-testid="garys-shed-index-hydrating"
                        aria-hidden="true"
                    >
                        &nbsp;
                    </div>
                ) : indexPage ? (
                    <div
                        className="prose prose-sm max-w-none font-body text-ink leading-relaxed"
                        data-testid="garys-shed-index-body"
                    >
                        <ReactMarkdown components={_MD_COMPONENTS} rehypePlugins={_REHYPE_PLUGINS}>
                            {indexPage.body_md}
                        </ReactMarkdown>
                    </div>
                ) : err ? (
                    <div className="font-mono text-xs text-stamp-red" data-testid="garys-shed-error">{err}</div>
                ) : fallbackList.length === 0 ? (
                    <div className="font-body text-sm text-ink-soft" data-testid="garys-shed-empty">
                        Nothing here yet.
                    </div>
                ) : (
                    <ul className="space-y-3" data-testid="garys-shed-list">
                        {fallbackList.map((p) => (
                            <li key={`${p.slug}-${p.version}`}>
                                <Link
                                    to={`${prefix}/${p.slug}`}
                                    className="font-heading uppercase tracking-[0.18em] text-base text-ink hover:text-stamp-red transition-colors"
                                    data-testid={`garys-shed-link-${p.slug}`}
                                >
                                    {p.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export function GarysShedPage() {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/api/garys-shed/page/${slug}`);
                if (!cancelled) setPage(data);
            } catch (e) {
                if (!cancelled) {
                    if (e.response?.status === 404) setErr("Not here.");
                    else setErr(`Couldn't load: ${e.message}`);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    const prefix = _routePrefix();

    useDocumentMeta({
        title: page?.title ? `${page.title} · Gary's Shed` : "Gary's Shed",
        description: page?.og_description || "",
    });

    return (
        <div className="min-h-screen bg-paper text-ink public-site" data-testid={`garys-shed-page-${slug}`}>
            <div className="max-w-2xl mx-auto px-6 py-16">
                <div className="mb-8">
                    <Link
                        to={prefix || "/"}
                        className="font-mono text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-stamp-red transition-colors"
                        data-testid="garys-shed-back-link"
                    >
                        ← back
                    </Link>
                </div>
                {loading ? (
                    <div className="font-mono text-xs text-ink-soft">loading…</div>
                ) : err ? (
                    <div data-testid="garys-shed-page-error">
                        <h1 className="font-heading uppercase tracking-[0.22em] text-2xl mb-3">{err}</h1>
                    </div>
                ) : page ? (
                    <article>
                        <h1
                            className="font-heading uppercase tracking-[0.22em] text-3xl mb-8"
                            data-testid="garys-shed-page-title"
                        >
                            {page.title}
                        </h1>
                        <div
                            className="prose prose-sm max-w-none font-body text-ink leading-relaxed"
                            data-testid="garys-shed-page-body"
                        >
                            <ReactMarkdown components={_MD_COMPONENTS} rehypePlugins={_REHYPE_PLUGINS}>
                                {page.body_md}
                            </ReactMarkdown>
                        </div>
                    </article>
                ) : null}
            </div>
        </div>
    );
}
